import {
  areWindowsEqual,
  createLastNWindow,
  createRelativeWindow,
  DataTable,
  DateGranularity,
  DateTrunc,
  dateTruncFromGranularity,
  DateWindow,
  dateWindowOptions,
  DateWindowTypes,
} from '@tableau/metrics-engine';
import * as React from 'react';
import { DateWindowCustomComponent } from './DateWindowCustomComponent';
import { DateWindowTypeDropdownComponent } from './DateWindowTypeDropdownComponent';

export interface DateWindowComponentProps {
  dateGranularity: DateGranularity;
  data: DataTable;
  selectedDateWindow: DateWindow | null;
  onChange: (value: DateWindow | null) => void;
  validationCounter?: number;
  disabled?: boolean;
  dropdownRef?: React.RefObject<HTMLButtonElement>;
}

export const DateWindowComponent = (props: DateWindowComponentProps): JSX.Element => {
  const dateTrunc = dateTruncFromGranularity(props.dateGranularity);
  const willShowCustomWindowSelected = shouldShowCustomWindowSelected(props.selectedDateWindow, props.dateGranularity);
  const initialSelectedCustomWindow = canShowWindowAsCustom(props.selectedDateWindow)
    ? props.selectedDateWindow
    : defaultCustomWindow(dateTrunc);

  const [isCustomWindowSelected, setIsCustomWindowSelected] = React.useState<boolean>(willShowCustomWindowSelected);
  const [lastCustomDateWindow, setLastCustomDateWindow] = React.useState<DateWindow | null>(
    initialSelectedCustomWindow
  );
  React.useEffect(() => {
    if (isCustomWindowSelected) {
      if (!windowsMatch(props.selectedDateWindow, lastCustomDateWindow)) {
        props.onChange(lastCustomDateWindow);
      }
    }
  }, [isCustomWindowSelected, lastCustomDateWindow]);

  const onCustomDateWindowChanged = (window: DateWindow | null) => {
    if (isCustomWindowSelected) {
      setLastCustomDateWindow(window);
    }
  };

  return (
    <>
      <DateWindowTypeDropdownComponent
        testId={'date-window-type-dropdown'}
        setCustomWindowSelected={setIsCustomWindowSelected}
        isCustomWindowSelected={isCustomWindowSelected}
        dateTrunc={dateTrunc}
        selectedDateWindow={props.selectedDateWindow}
        onChange={props.onChange}
        disabled={props.disabled}
        dropdownRef={props.dropdownRef}
      />
      {isCustomWindowSelected && (
        <DateWindowCustomComponent
          testId={'date-window-custom'}
          dateTrunc={dateTrunc}
          data={props.data}
          onChange={onCustomDateWindowChanged}
          selectedDateWindow={props.selectedDateWindow}
          validationCounter={props.validationCounter}
          disabled={props.disabled}
        />
      )}
    </>
  );
};

/**
 * Our date window picker shows both "presets", as well as "custom" input controls.
 * The "relative date" preset windows can also be expressed via the "custom" UI.
 *
 * This differs from `shouldShowCustomWindowSelected`, as that function only
 * returns true if the given window does not appear in the preset list. This
 * function ignores the preset list entirely.
 *
 * @param selectedWindow the window to test
 * @returns `true` if the `selectedWindow` can be expressed via the "Custom"
 * window picker UI.
 */
const canShowWindowAsCustom = (selectedWindow: DateWindow | null): boolean => {
  if (selectedWindow === null) {
    return false;
  }
  if (selectedWindow.type === DateWindowTypes.SPECIAL) {
    return false;
  }

  return true;
};

/**
 *
 * @param selectedWindow the window to test
 * @param granularity the granularity of the metric
 * @returns `true` if the `selectedWindow` should result in "Custom" being
 * initially selected in the date picker dropdown
 */
export const shouldShowCustomWindowSelected = (
  selectedWindow: DateWindow | null,
  granularity: DateGranularity
): boolean => {
  if (selectedWindow === null) {
    return false;
  }
  if (selectedWindow.type === DateWindowTypes.CUSTOM) {
    return true;
  }
  if (selectedWindow.type === DateWindowTypes.DEFINED_START) {
    return true;
  }
  if (selectedWindow.type === DateWindowTypes.SPECIAL) {
    // There is only one "Special" window type: All Time
    return false;
  }
  const defaultOptions = dateWindowOptions(dateTruncFromGranularity(granularity));
  const indexOfWindowInDefaultList = defaultOptions.findIndex((window) => areWindowsEqual(window, selectedWindow));

  if (indexOfWindowInDefaultList === -1) {
    return true;
  }

  return false;
};

export const windowsMatch = (window: DateWindow | null, other: DateWindow | null): boolean => {
  if (window === null && other === null) {
    return true;
  }
  if (window === null || other === null) {
    return false;
  }

  return areWindowsEqual(window, other);
};

function defaultCustomWindow(dateTrunc: DateTrunc): DateWindow {
  return dateTrunc === DateTrunc.UNKNOWN
    ? createLastNWindow(1)
    : createRelativeWindow({ unit: dateTrunc, numberOfUnits: 1 });
}
